import { useState, useRef, useCallback } from 'react'
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import Modal, { ModalHeader, ModalBody, ModalFooter } from './Modal'
import Button from './Button'
import { ImagePlus } from 'lucide-react'

export type CropResult = {
  file: File
  previewUrl: string
}

type ImageCropModalProps = {
  open: boolean
  onClose: () => void
  onCropComplete: (result: CropResult) => void
  aspectRatio?: number
  circularCrop?: boolean
  loading?: boolean
  title?: string
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): Crop {
  return centerCrop(
    makeAspectCrop(
      { unit: '%', width: 90 },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

export default function ImageCropModal({ open, onClose, onCropComplete, aspectRatio, circularCrop, loading, title = 'Recortar Imagem' }: ImageCropModalProps) {
  const [imgSrc, setImgSrc] = useState('')
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleSelectFile() {
    fileInputRef.current?.click()
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFile(file)
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      setImgSrc(reader.result?.toString() || '')
    })
    reader.readAsDataURL(file)
  }

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget
    if (aspectRatio) {
      setCrop(centerAspectCrop(naturalWidth, naturalHeight, aspectRatio))
    } else {
      setCrop(centerCrop({ unit: '%', width: 90, height: 90 }, naturalWidth, naturalHeight))
    }
  }, [aspectRatio])

  function handleCropAndSave() {
    const image = imgRef.current
    if (!image || !completedCrop || !selectedFile) return

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const pixelCrop = {
      x: Math.round(completedCrop.x * scaleX),
      y: Math.round(completedCrop.y * scaleY),
      width: Math.round(completedCrop.width * scaleX),
      height: Math.round(completedCrop.height * scaleY),
    }

    const canvas = document.createElement('canvas')
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    )

    const previewUrl = canvas.toDataURL('image/png')

    canvas.toBlob((blob) => {
      if (!blob) return
      const croppedFile = new File([blob], 'image.png', { type: 'image/png' })
      onCropComplete({ file: croppedFile, previewUrl })
    }, 'image/png')
  }

  function handleClose() {
    setImgSrc('')
    setCrop(undefined)
    setCompletedCrop(undefined)
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} size="lg">
      <ModalHeader onClose={handleClose}>{title}</ModalHeader>
      <ModalBody>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleFileChange}
        />

        {!imgSrc ? (
          <button
            type="button"
            onClick={handleSelectFile}
            className="flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 py-16 transition-colors hover:border-brand-400 hover:bg-brand-25"
          >
            <ImagePlus className="h-10 w-10 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">
              Clique para selecionar uma imagem
            </span>
            <span className="text-xs text-gray-400">
              PNG, JPG ou WebP
            </span>
          </button>
        ) : (
          <div className="space-y-3">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio}
              circularCrop={circularCrop}
              className="mx-auto max-h-[60vh]"
            >
              <img
                ref={imgRef}
                src={imgSrc}
                alt="Imagem para recorte"
                onLoad={onImageLoad}
                className="max-h-[60vh] w-auto"
              />
            </ReactCrop>
            <button
              type="button"
              onClick={handleSelectFile}
              className="text-xs font-medium text-brand-600 hover:text-brand-700 transition-colors"
            >
              Trocar imagem
            </button>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleCropAndSave}
          disabled={!completedCrop || loading}
          loading={loading}
        >
          Cortar e Salvar
        </Button>
      </ModalFooter>
    </Modal>
  )
}
